import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { OcsrRecognitionError, recognizeStructureFromImage, type OcsrFailureKind } from '@peruselab/api-client/ocsr';
import { downscaleImageIfNeeded } from '@peruselab/structure-editor/domain/imageDownscale';
import { importSmiles } from '@peruselab/structure-editor/chemistry/smilesImport';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';

type OcsrRecognitionState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'imported'; readonly canonicalSmiles: string }
  | { readonly state: 'error'; readonly kind: OcsrFailureKind | 'drawingChanged' };

/** #1028: an image has its own request/result state; it never populates the SMILES form. */
export function useOcsrImport(
  paperSize: PaperSize,
  onRecognized: (document: DrawingDocument, sourceFile: File) => boolean,
  contextKey: string,
  active: boolean
) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [handDrawn, setHandDrawn] = useState(false);
  const [ocsrRecognitionState, setOcsrRecognitionState] = useState<OcsrRecognitionState>({ state: 'idle' });
  const generation = useRef(0);
  const loading = useRef(false);
  const current = useRef({ onRecognized, contextKey, active });
  useLayoutEffect(() => {
    current.current = { onRecognized, contextKey, active };
  }, [onRecognized, contextKey, active]);

  const reset = useCallback(() => {
    generation.current++;
    loading.current = false;
    setSelectedFile(null);
    setHandDrawn(false);
    setOcsrRecognitionState({ state: 'idle' });
  }, []);
  useEffect(() => {
    if (!active) reset();
  }, [active, reset]);
  useEffect(
    () => () => {
      generation.current++;
    },
    []
  );

  const updateSelectedFile = useCallback((file: File | null) => {
    generation.current++;
    loading.current = false;
    setSelectedFile(file);
    setOcsrRecognitionState({ state: 'idle' });
  }, []);

  const handleOcsrImport = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!selectedFile || loading.current || !active) return;
      const request = ++generation.current;
      const source = current.current.contextKey;
      loading.current = true;
      setOcsrRecognitionState({ state: 'loading' });
      const isCurrent = () => request === generation.current && current.current.active;
      try {
        const uploadFile = await downscaleImageIfNeeded(selectedFile);
        if (!isCurrent()) return;
        const smiles = await recognizeStructureFromImage(uploadFile, handDrawn);
        if (!isCurrent()) return;
        const result = await importSmiles(smiles, paperSize);
        if (!isCurrent()) return;
        if (current.current.contextKey !== source) {
          setOcsrRecognitionState({ state: 'error', kind: 'drawingChanged' });
          return;
        }
        // The workspace can defer switching until the current drawing is saved or discarded.
        const imported = current.current.onRecognized(result.document, selectedFile);
        setSelectedFile(null);
        setOcsrRecognitionState(
          imported ? { state: 'imported', canonicalSmiles: result.canonicalSmiles } : { state: 'idle' }
        );
      } catch (error) {
        if (isCurrent())
          setOcsrRecognitionState({
            state: 'error',
            kind: error instanceof OcsrRecognitionError ? error.kind : 'unknown'
          });
      } finally {
        if (isCurrent()) loading.current = false;
      }
    },
    [selectedFile, handDrawn, paperSize, active]
  );

  return { selectedFile, updateSelectedFile, handDrawn, setHandDrawn, ocsrRecognitionState, handleOcsrImport, reset };
}
